import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  X,
  Sparkles,
  MonitorPlay,
} from "lucide-react";

import expertiseData from "../data/expertise.json";
import { Link } from "react-router-dom";

// --- NETFLIX STYLE CINEMATIC GALLERY ---
const CinematicGallery = ({ items }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const videoRefs = useRef([]);

  // Auto-slide logic
  useEffect(() => {
    if (isPaused || items.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isPaused, items.length]);

  useEffect(() => {
    videoRefs.current.forEach((video, idx) => {
      if (video) {
        if (idx === currentIndex) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [currentIndex]);

  return (
    <div
      className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(147,51,234,0.15)] group bg-[#020617] border border-white/10"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={() => setIsPaused(true)}
      onTouchEnd={() => setIsPaused(false)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className="absolute inset-0 w-full h-full"
        >
          {items[currentIndex].type === "video" ? (
            <video
              ref={(el) => (videoRefs.current[currentIndex] = el)}
              src={items[currentIndex].url}
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={items[currentIndex].url}
              alt="Showcase"
              className="w-full h-full object-cover"
              draggable={false}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-transparent to-[#020617]/40 opacity-80 pointer-events-none"></div>
        </motion.div>
      </AnimatePresence>

      <div className="absolute bottom-4 left-4 right-4 flex gap-2 z-20">
        {items.map((_, idx) => (
          <div
            key={idx}
            className="h-1 flex-1 bg-white/20 rounded-full overflow-hidden cursor-pointer"
            onClick={() => setCurrentIndex(idx)}
          >
            <motion.div
              className="h-full bg-purple-500 rounded-full"
              initial={{ width: "0%" }}
              animate={{
                width:
                  currentIndex === idx
                    ? "100%"
                    : currentIndex > idx
                      ? "100%"
                      : "0%",
              }}
              transition={{
                duration: currentIndex === idx && !isPaused ? 4 : 0.3,
                ease: "linear",
              }}
            />
          </div>
        ))}
      </div>

      <div className="absolute top-4 right-4 z-20 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="w-8 h-8 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center border border-white/10 text-white">
          <MonitorPlay className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
};

// --- DETAIL PANEL CONTENT ---
const DetailContent = ({ data }) => {
  return (
    <div className="flex flex-col gap-10">
      <CinematicGallery items={data.gallery} />

      <div>
        <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4 hidden lg:block">
          {data.title.replace("\n", " ")}
        </h3>
        <p className="text-xl text-slate-300 font-light leading-relaxed">
          {data.description}
        </p>
      </div>

      <div>
        <h4 className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-4">
          Tech Arsenal
        </h4>
        <div className="flex flex-wrap gap-2">
          {data.technologies.map((tech, i) => (
            <div
              key={i}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm font-medium text-slate-200 hover:bg-white/10 hover:border-purple-500/30 transition-all shadow-[0_0_15px_rgba(255,255,255,0.02)]"
            >
              <span className="text-lg">{tech.icon}</span>
              {tech.name}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#0f172a]/50 p-8 rounded-3xl border border-white/10 shadow-[inset_0_0_20px_rgba(255,255,255,0.02)]">
          <h4 className="text-sm font-bold tracking-widest text-purple-400 uppercase mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-purple-500/50"></span>
            What I Offer
          </h4>
          <ul className="flex flex-col gap-5">
            {data.offers.map((offer, i) => (
              <li
                key={i}
                className="flex items-center gap-4 text-slate-200 text-base font-medium"
              >
                <CheckCircle2 className="w-5 h-5 text-purple-500 shrink-0" />
                <span>{offer}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-[#0f172a]/50 p-8 rounded-3xl border border-white/10 shadow-[inset_0_0_20px_rgba(255,255,255,0.02)]">
          <h4 className="text-sm font-bold tracking-widest text-blue-400 uppercase mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-blue-500/50"></span>
            Deliverables
          </h4>
          <ul className="flex flex-col gap-5">
            {data.deliverables.map((item, i) => (
              <li
                key={i}
                className="flex items-center gap-4 text-slate-200 text-base font-medium"
              >
                <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-6">
          The Process
        </h4>
        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-400 font-medium">
          {data.process.map((step, i) => (
            <React.Fragment key={i}>
              <span className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-300">
                {step}
              </span>
              {i < data.process.length - 1 && (
                <ArrowRight className="w-4 h-4 text-slate-600" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 p-8 rounded-3xl border border-purple-500/20 bg-gradient-to-br from-purple-500/10 to-blue-500/5 relative overflow-hidden group">
        <div className="absolute inset-0 bg-blue-500/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
        <div className="relative z-10">
          <p className="text-xs font-bold tracking-widest text-purple-400 uppercase mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Ready to Start?
          </p>
          <p className="text-sm text-slate-300">
            Most projects start around{" "}
            <span className="text-white font-bold text-xl ml-1">
              {data.pricing}
            </span>
          </p>
        </div>
        <Link
          to={`https://wa.me/917201840095?text=${encodeURIComponent(data?.message)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <button className="relative z-10 px-8 py-4 font-bold text-white rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 hover:scale-105 transition-all duration-300 shadow-[0_0_20px_rgba(147,51,234,0.3)] hover:shadow-[0_0_30px_rgba(147,51,234,0.6)] flex items-center justify-center gap-2">
            Let's Discuss <ChevronRight className="w-5 h-5" />
          </button>
        </Link>
      </div>
    </div>
  );
};

// --- DESKTOP LAYOUT (CUSTOM DYNAMIC PINNING) ---
const DesktopExpertise = () => {
  const [activeId, setActiveId] = useState(expertiseData[0].id);
  const sectionRefs = useRef({});

  // Custom Pinning Refs & State
  const containerRef = useRef(null);
  const leftColRef = useRef(null);
  const [pinState, setPinState] = useState("relative");
  const [leftOffset, setLeftOffset] = useState(0);
  const [colWidth, setColWidth] = useState(0);

  // Auto-Scrolling Menu State
  const menuWrapperRef = useRef(null);
  const menuItemRefs = useRef([]);
  const [menuY, setMenuY] = useState(0);

  // 1. Logic for Scroll Spy (Updating Active Menu)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        root: null,
        rootMargin: "-30% 0px -60% 0px", // Trigger highlight past center
        threshold: 0,
      },
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // 2. Logic for Auto-Centering the Menu List
  useEffect(() => {
    const activeIndex = expertiseData.findIndex((item) => item.id === activeId);

    if (menuWrapperRef.current && menuItemRefs.current[activeIndex]) {
      const container = menuWrapperRef.current;
      const activeItem = menuItemRefs.current[activeIndex];

      // Calculate perfect center
      const containerCenter = container.offsetHeight / 2;
      const itemCenter = activeItem.offsetTop + activeItem.offsetHeight / 2;

      // Calculate translation offset to bring item to center
      const offset = containerCenter - itemCenter;
      setMenuY(offset);
    }
  }, [activeId]);

  // 3. Custom Logic for Dynamic Position Fixed
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || !leftColRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const colRect = leftColRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Ensure the fixed menu exactly matches the width and position of its container
      setLeftOffset(colRect.left);
      setColWidth(colRect.width);

      if (containerRect.top > 0) {
        setPinState("relative");
      } else if (
        containerRect.top <= 0 &&
        containerRect.bottom >= windowHeight
      ) {
        setPinState("fixed");
      } else {
        setPinState("bottom");
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    handleScroll(); // Trigger initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const handleScrollTo = (id) => {
    const element = sectionRefs.current[id];
    if (element) {
      const yOffset = -50;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <div
      ref={containerRef}
      className="max-w-[1600px] mx-auto w-full px-6 md:px-12 lg:px-20 flex relative z-10 pb-[20vh]"
    >
      {/* LEFT: Custom Dynamic Sticky Wrapper */}
      <div ref={leftColRef} className="w-[45%] lg:w-[40%] xl:w-[35%] relative">
        {/* THE ACTUAL PINNED CONTENT */}
        <div
          className="flex flex-col pr-12 h-screen"
          style={{
            position: pinState === "fixed" ? "fixed" : "absolute",
            top:
              pinState === "fixed"
                ? "0px"
                : pinState === "relative"
                  ? "0px"
                  : "auto",
            bottom: pinState === "bottom" ? "0px" : "auto",
            left: pinState === "fixed" ? `${leftOffset}px` : "0px",
            width: pinState === "fixed" ? `${colWidth}px` : "100%",
            zIndex: 30,
          }}
        >
          {/* Static Header Section */}
          <div className="flex items-center gap-3 mb-8 pt-10 shrink-0">
            <div className="h-px w-12 bg-purple-500"></div>
            <span className="text-purple-400 font-medium tracking-widest uppercase text-sm">
              My Expertise / Services
            </span>
          </div>

          {/* Masked Sliding Window for the List */}
          <div
            ref={menuWrapperRef}
            className="relative w-full flex-1 overflow-hidden"
            style={{
              // This creates a smooth fade out effect at the top and bottom of the list!
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
              maskImage:
                "linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)",
            }}
          >
            {/* The Actual Translated List */}
            <motion.div
              className="absolute top-0 w-full flex flex-col gap-2 pb-[50vh]" // padding bottom gives space to scroll the last item up
              animate={{ y: menuY }}
              transition={{ type: "spring", stiffness: 90, damping: 20 }}
            >
              {expertiseData.map((item, index) => {
                const isActive = activeId === item.id;
                return (
                  <button
                    key={item.id}
                    ref={(el) => (menuItemRefs.current[index] = el)} // Attach ref to each item
                    onClick={() => handleScrollTo(item.id)}
                    className="group relative w-full text-left py-6 outline-none transition-all duration-300"
                  >
                    <div className="absolute bottom-0 left-0 h-px bg-white/5 w-full transition-colors group-hover:bg-white/10"></div>

                    {/* Animated Active Line */}
                    <motion.div
                      className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-purple-500 to-blue-500"
                      initial={{ width: "0%" }}
                      animate={{ width: isActive ? "100%" : "0%" }}
                      transition={{ duration: 0.6, ease: "circOut" }}
                    />

                    <motion.h2
                      animate={{
                        opacity: isActive ? 1 : 0.25,
                        x: isActive ? 30 : 0,
                      }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="text-4xl lg:text-5xl xl:text-6xl font-thin leading-[1.1] tracking-tighter whitespace-pre-line uppercase"
                    >
                      {item.title}
                    </motion.h2>

                    {isActive && (
                      <div className="absolute inset-0 bg-purple-500/10 blur-[60px] -z-10 pointer-events-none rounded-full" />
                    )}
                  </button>
                );
              })}
            </motion.div>
          </div>
        </div>
      </div>

      {/* RIGHT: Native Vertical Scrolling Content */}
      <div className="w-[55%] lg:w-[60%] xl:w-[65%] flex flex-col pt-[15vh]">
        {expertiseData.map((item, index) => (
          <div
            key={item.id}
            id={item.id}
            ref={(el) => (sectionRefs.current[item.id] = el)}
            className="w-full mb-[20vh] relative group"
          >
            <div className="absolute -top-20 -left-10 text-[200px] font-black text-white/[0.02] pointer-events-none select-none">
              0{index + 1}
            </div>

            <div className="w-full h-auto rounded-3xl bg-[#0a0f1d]/90 backdrop-blur-md border border-white/10 shadow-2xl p-8 lg:p-12 relative z-20">
              <DetailContent data={item} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// --- MOBILE LAYOUT (WITH NEXT / PREV BUTTONS) ---
const MobileExpertise = () => {
  const [selectedId, setSelectedId] = useState(null);
  const selectedData = expertiseData.find((item) => item.id === selectedId);

  useEffect(() => {
    if (selectedId) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedId]);

  // Navigation Handlers for Drawer
  const handleNext = () => {
    if (!selectedId) return;
    const currentIndex = expertiseData.findIndex(
      (item) => item.id === selectedId,
    );
    const nextIndex = (currentIndex + 1) % expertiseData.length;
    setSelectedId(expertiseData[nextIndex].id);
  };

  const handlePrev = () => {
    if (!selectedId) return;
    const currentIndex = expertiseData.findIndex(
      (item) => item.id === selectedId,
    );
    const prevIndex =
      (currentIndex - 1 + expertiseData.length) % expertiseData.length;
    setSelectedId(expertiseData[prevIndex].id);
  };

  return (
    <div className="w-full px-6 py-24 relative z-10 min-h-screen flex flex-col justify-center">
      <div className="flex items-center gap-3 mb-12">
        <div className="h-px w-8 bg-purple-500"></div>
        <span className="text-purple-400 font-medium tracking-widest uppercase text-sm">
          My Expertise
        </span>
      </div>

      <div className="flex flex-col w-full">
        {expertiseData.map((item) => (
          <button
            key={item.id}
            onClick={() => setSelectedId(item.id)}
            className="w-full text-left py-8 border-b border-white/10 group relative"
          >
            <h2 className="text-4xl sm:text-5xl font-thin leading-[1.1] tracking-tighter whitespace-pre-line uppercase text-slate-200">
              {item.title}
            </h2>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-slate-400 group-active:scale-95 transition-all">
              <ChevronRight className="w-5 h-5" />
            </div>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {selectedId && selectedData && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{
              type: "spring",
              damping: 30,
              stiffness: 300,
              mass: 0.8,
            }}
            className="fixed inset-0 z-50 flex flex-col bg-[#020617]"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 bg-[#020617]/90 backdrop-blur-md z-20">
              <h3 className="text-sm font-bold tracking-widest text-purple-400 uppercase truncate pr-4">
                {selectedData.title.replace("\n", " ")}
              </h3>
              <button
                onClick={() => setSelectedId(null)}
                className="p-2 rounded-full bg-white/10 active:bg-white/20 transition-colors text-white shrink-0"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto bg-[#020617] p-6 pb-40">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedId}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <DetailContent data={selectedData} />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Next / Prev Navigation Bottom Bar */}
            <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-[#020617] via-[#020617] to-transparent flex justify-between items-center z-30">
              <button
                onClick={handlePrev}
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur-md text-white font-medium hover:bg-white/20 active:scale-95 transition-all"
              >
                <ChevronLeft className="w-5 h-5" /> Prev
              </button>
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-5 py-3 rounded-full bg-purple-600 text-white font-medium hover:bg-purple-500 active:scale-95 transition-all shadow-[0_0_20px_rgba(147,51,234,0.3)]"
              >
                Next <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- MAIN EXPORT ---
const Expertise = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section className="relative text-slate-200 border-t border-white/5 bg-transparent">
      {isMobile ? <MobileExpertise /> : <DesktopExpertise />}
    </section>
  );
};

export default Expertise;
